import { datasetToCsv } from "./csv";
import { datasetToHtml } from "./html";
import type { ExportDataset } from "./types";

/**
 * Browser-side delivery helpers. These touch the DOM, so they only run in a
 * window context (web + Tauri WebView). The pure serializers (`datasetToCsv`,
 * `datasetToHtml`) stay DOM-free so they're trivially unit-testable.
 */

/** Triggers a file download for arbitrary text content via a Blob + anchor. */
export function downloadTextFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener noreferrer";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoke after the click has consumed the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Slugifies a title into a safe filename stem. */
function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-+|-+$/g, "")
      .slice(0, 60) || "export"
  );
}

/** Builds `<slug>-<yyyy-mm-dd>` from the dataset metadata. */
function defaultStem(dataset: ExportDataset): string {
  const stem = slugify(dataset.metadata.title ?? "export");
  const date = (dataset.metadata.generatedAt ?? new Date().toISOString()).slice(0, 10);
  return `${stem}-${date}`;
}

/** Serializes the dataset to CSV and downloads it as a `.csv` file. */
export function exportDatasetAsCsv(dataset: ExportDataset, filename?: string): void {
  const name = filename ?? `${defaultStem(dataset)}.csv`;
  downloadTextFile(name, datasetToCsv(dataset), "text/csv");
}

/**
 * Renders the dataset to a print-ready HTML document and opens it in a new
 * window, triggering the browser's print dialog ("Save as PDF"). Falls back to
 * downloading the HTML file if a popup can't be opened.
 */
export function exportDatasetAsPdf(dataset: ExportDataset, filename?: string): void {
  const html = datasetToHtml(dataset);
  const win = window.open("", "_blank");
  if (!win) {
    // Popup blocked (or no window, e.g. tests/headless) → hand off the HTML.
    downloadTextFile(filename ?? `${defaultStem(dataset)}.html`, html, "text/html");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Give the new document a tick to lay out before invoking print.
  win.addEventListener("load", () => {
    win.focus();
    win.print();
  });
}
